import { motion } from 'framer-motion';
import {
  BarChart3,
  CheckCircle,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  PauseCircle as PauseCircleIcon,
  PlayCircle,
  Rocket as RocketLaunchIcon,
  TrendingUp,
  Users as UserGroupIcon,
  XCircle,
  XCircle as XCircleIcon,
} from 'lucide-react';

import React from 'react';

interface FunnelStage {
  stageName: string;
  count: number;
}

interface ChurnBreakdown {
  type: string;
  count: number;
}

interface FunnelChartProps {
  data: FunnelStage[];
  churnData?: ChurnBreakdown[];
}

const FunnelChart: React.FC<FunnelChartProps> = ({ data, churnData }) => {
  // Stage configuration with colors and icons - IN CORRECT ORDER
  const stageConfig: Record<
    string,
    {
      color: string;
      bgColor: string;
      borderColor: string;
      icon: React.ComponentType<any>;
      order: number;
    }
  > = {
    Paused: {
      color: '#F59E0B',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-amber-200 dark:border-amber-800/30',
      icon: PauseCircleIcon,
      order: 1,
    },
    Handoff: {
      color: '#3B82F6',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-blue-200 dark:border-blue-800/30',
      icon: UserGroupIcon,
      order: 2,
    },
    Kickoff: {
      color: '#8B5CF6',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-purple-200 dark:border-purple-800/30',
      icon: RocketLaunchIcon,
      order: 3,
    },
    'Pre-Go Live': {
      color: '#7C3AED',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-violet-200 dark:border-violet-800/30',
      icon: ClockIcon,
      order: 4,
    },
    'Go-Live': {
      color: '#10B981',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-emerald-200 dark:border-emerald-800/30',
      icon: RocketLaunchIcon,
      order: 5,
    },
    'Closed Won': {
      color: '#10B981',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-emerald-200 dark:border-emerald-800/30',
      icon: CheckCircleIcon,
      order: 5,
    },
    'Closed Lost': {
      color: '#EF4444',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-red-200 dark:border-red-800/30',
      icon: XCircleIcon,
      order: 6,
    },
  };

  // Create a complete dataset with all stages, filling in 0 for missing stages
  const allStageNames = [
    'Paused',
    'Handoff',
    'Kickoff',
    'Pre-Go Live',
    'Go-Live',
    'Closed Won',
    'Closed Lost',
  ];
  const completeData: FunnelStage[] = allStageNames.map(stageName => {
    const existingStage = data.find(s => s.stageName === stageName);

    return {
      stageName,
      count: existingStage?.count || 0,
    };
  });

  // Calculate totals
  const totalInPipeline = completeData
    .filter(stage => stage.stageName !== 'Closed Won' && stage.stageName !== 'Closed Lost')
    .reduce((sum, stage) => sum + stage.count, 0);

  const closedWon = completeData.find(stage => stage.stageName === 'Closed Won')?.count || 0;
  const closedLost = completeData.find(stage => stage.stageName === 'Closed Lost')?.count || 0;
  const totalCompleted = closedWon + closedLost;
  const successRate = totalCompleted > 0 ? ((closedWon / totalCompleted) * 100).toFixed(1) : '0.0';
  const failureRate = totalCompleted > 0 ? ((closedLost / totalCompleted) * 100).toFixed(1) : '0.0';

  // Get churn breakdown
  const logoChurn = churnData?.find(item => item.type === 'Logo Churn')?.count || 0;
  const productChurn = churnData?.find(item => item.type === 'Product Churn')?.count || 0;

  // Split into pipeline and exit stages (now using completeData)
  const pipelineStages = completeData.filter(
    stage => stage.stageName !== 'Closed Won' && stage.stageName !== 'Closed Lost'
  );
  const exitStages = completeData.filter(
    stage => stage.stageName === 'Closed Won' || stage.stageName === 'Closed Lost'
  );

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
          Onboarding Pipeline Journey
        </h2>
      </motion.div>

      {/* Vertical Timeline */}
      <div className="relative">
        {/* Pipeline Stages */}
        <div className="space-y-6 mb-12 relative">
          {/* Vertical Line - extended to cover all stages including exit stages */}
          <div
            className="absolute left-8 w-1.5 rounded-full bg-gradient-to-b from-amber-400 via-blue-400 via-purple-400 via-violet-500 to-emerald-500 dark:from-amber-500 dark:via-blue-500 dark:via-purple-500 dark:via-violet-600 dark:to-emerald-600 shadow-lg"
            style={{ top: '3rem', height: 'calc(100% + 4rem)' }}
          >
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-b from-amber-400 via-blue-400 via-purple-400 via-violet-500 to-emerald-500 blur-sm opacity-50"></div>
          </div>

          {pipelineStages.map((stage, index) => {
            const config = stageConfig[stage.stageName];
            const Icon = config?.icon;
            const percentage =
              totalInPipeline > 0 ? ((stage.count / totalInPipeline) * 100).toFixed(1) : '0.0';

            return (
              <motion.div
                key={stage.stageName}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="relative flex items-center gap-6 pl-20"
              >
                {/* Timeline Dot - centered with enhanced styling */}
                <motion.div
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  className="absolute left-4 w-10 h-10 rounded-full border-4 border-white dark:border-gray-900 shadow-2xl flex items-center justify-center z-10"
                  style={{ backgroundColor: config?.color }}
                >
                  <span className="text-white font-extrabold text-sm">{index + 1}</span>
                  {/* Inner glow */}
                  <div
                    className="absolute inset-0 rounded-full opacity-40 blur-md"
                    style={{ backgroundColor: config?.color }}
                  ></div>
                </motion.div>

                {/* Stage Card */}
                <motion.div
                  whileHover={{ scale: 1.03, x: 10, y: -3 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className={`flex-1 ${config?.bgColor} border-2 ${config?.borderColor} rounded-2xl p-4 shadow-sm hover:shadow-2xl backdrop-blur-sm relative overflow-hidden`}
                >
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent dark:from-white/5 pointer-events-none"></div>

                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Icon with glow effect */}
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg border-2"
                        style={{
                          backgroundColor: `${config?.color}15`,
                          borderColor: `${config?.color}40`,
                        }}
                      >
                        {Icon && (
                          <Icon
                            className="w-6 h-6"
                            style={{ color: config?.color }}
                            strokeWidth={2.5}
                          />
                        )}
                      </div>

                      {/* Stage Info */}
                      <div>
                        <h3 className="text-lg font-extrabold text-gray-900 dark:text-white tracking-tight mb-0.5">
                          {stage.stageName}
                        </h3>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Stage {index + 1}
                        </p>
                      </div>
                    </div>

                    {/* Count and Percentage */}
                    <div className="text-right">
                      <div
                        className="text-4xl font-black mb-1 tracking-tight"
                        style={{ color: config?.color }}
                      >
                        {stage.count}
                      </div>
                      <div
                        className="text-xs font-semibold px-2.5 py-1 rounded-full inline-block"
                        style={{
                          backgroundColor: `${config?.color}20`,
                          color: config?.color,
                        }}
                      >
                        {percentage}%
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Exit Stages */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: pipelineStages.length * 0.15 + 0.4, duration: 0.5 }}
          className="pl-20 mt-6"
        >
          <div className="space-y-4">
            {/* Main Cards Row - Side by Side with Numbers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {exitStages.map((stage, index) => {
                const config = stageConfig[stage.stageName];
                const Icon = config?.icon;
                const isSuccess = stage.stageName === 'Closed Won';
                const rate = isSuccess ? successRate : failureRate;
                const stageNumber = index + 6; // 6 for Closed Won, 7 for Closed Lost

                return (
                  <motion.div
                    key={stage.stageName}
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: pipelineStages.length * 0.15 + 0.2 + index * 0.1,
                      duration: 0.5,
                    }}
                    className="relative flex items-center gap-6"
                  >
                    {/* Timeline Dot with Number - Only show for Closed Won (6) */}
                    {isSuccess && (
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                        className="absolute -left-16 w-10 h-10 rounded-full border-4 border-white dark:border-gray-900 shadow-2xl flex items-center justify-center z-10"
                        style={{ backgroundColor: config?.color }}
                      >
                        <span className="text-white font-extrabold text-sm">{stageNumber}</span>
                        {/* Inner glow */}
                        <div
                          className="absolute inset-0 rounded-full opacity-40 blur-md"
                          style={{ backgroundColor: config?.color }}
                        ></div>
                      </motion.div>
                    )}

                    {/* Stage Card */}
                    <motion.div
                      whileHover={{ scale: 1.03, y: -3 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      className="flex-1 relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border-2 shadow-sm hover:shadow-2xl"
                      style={{ borderColor: config?.color }}
                    >
                      {/* Subtle gradient background */}
                      <div
                        className="absolute inset-0 opacity-[0.05]"
                        style={{
                          background: `linear-gradient(135deg, ${config?.color} 0%, transparent 100%)`,
                        }}
                      />

                      {/* Content - Horizontal Layout */}
                      <div className="relative p-5 flex items-center justify-between gap-4">
                        {/* Left section: Icon + Rate */}
                        <div className="flex items-center gap-3">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md border-2"
                            style={{
                              backgroundColor: `${config?.color}15`,
                              borderColor: `${config?.color}30`,
                            }}
                          >
                            {Icon && (
                              <Icon
                                className="w-6 h-6"
                                style={{ color: config?.color }}
                                strokeWidth={2.5}
                              />
                            )}
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {isSuccess ? 'Success Rate' : 'Failure Rate'}
                            </div>
                            <div className="text-2xl font-black" style={{ color: config?.color }}>
                              {rate}%
                            </div>
                          </div>
                        </div>

                        {/* Right section: Stage name + Count */}
                        <div className="text-right">
                          <div
                            className="text-lg font-extrabold mb-1 uppercase tracking-tight"
                            style={{ color: config?.color }}
                          >
                            {stage.stageName}
                          </div>
                          <div className="flex items-baseline gap-1 justify-end">
                            <div
                              className="text-4xl font-black leading-none"
                              style={{ color: config?.color }}
                            >
                              {stage.count}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                              clinics
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>

            {/* Churn Breakdown - Separate box below Closed Lost (right column) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div></div> {/* Empty left column */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: pipelineStages.length * 0.15 + 0.6, duration: 0.5 }}
                whileHover={{ scale: 1.02, y: -3 }}
                className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border-2 border-red-200 dark:border-red-800/30 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="p-5">
                  <div className="text-sm font-extrabold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <div className="w-1 h-4 bg-red-500 rounded-full"></div>
                    Breakdown
                  </div>
                  <div className="space-y-3">
                    {/* Logo Churn */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm" />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Logo Churn
                        </span>
                      </div>
                      <div className="text-right flex items-baseline gap-1.5">
                        <span className="text-lg font-black text-red-600">{logoChurn}</span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600">
                          {closedLost > 0
                            ? `${((logoChurn / closedLost) * 100).toFixed(1)}%`
                            : '0%'}
                        </span>
                      </div>
                    </div>
                    {/* Product Churn */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-orange-500 shadow-sm" />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Product Churn
                        </span>
                      </div>
                      <div className="text-right flex items-baseline gap-1.5">
                        <span className="text-lg font-black text-orange-600">{productChurn}</span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600">
                          {closedLost > 0
                            ? `${((productChurn / closedLost) * 100).toFixed(1)}%`
                            : '0%'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg"
      >
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-600" strokeWidth={2.5} />
          Summary Statistics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm"
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                <PlayCircle className="w-5 h-5 text-blue-600" strokeWidth={2.5} />
              </div>
            </div>
            <div className="text-2xl font-black text-blue-600 mb-1">{totalInPipeline}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">In Pipeline</div>
          </motion.div>
          <motion.div
            className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm"
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                <CheckCircle className="w-5 h-5 text-purple-600" strokeWidth={2.5} />
              </div>
            </div>
            <div className="text-2xl font-black text-purple-600 mb-1">{totalCompleted}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
              Total Completed
            </div>
          </motion.div>
          <motion.div
            className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm"
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30">
                <TrendingUp className="w-5 h-5 text-green-600" strokeWidth={2.5} />
              </div>
            </div>
            <div className="text-2xl font-black text-green-600 mb-1">{successRate}%</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Success Rate</div>
          </motion.div>
          <motion.div
            className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm"
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/30">
                <XCircle className="w-5 h-5 text-red-600" strokeWidth={2.5} />
              </div>
            </div>
            <div className="text-2xl font-black text-red-600 mb-1">{failureRate}%</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Failure Rate</div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default FunnelChart;
